/**
 * The daily bar chart, hand-rolled.
 *
 * No chart library, matching DragDuro's traffic and cost dashboards: this draws
 * one series of daily values and nothing else, which is a flexbox row of divs
 * with percentage heights. A charting dependency would be several hundred
 * kilobytes to do less than this file does, and would still need styling to
 * match the theme.
 *
 * The axis treatment is deliberately austere, also lifted from DragDuro: three
 * y-values (max, half, zero) and two x-labels (first day, last day). Gridlines
 * and per-bar labels on a 90-bar series are noise — the shape is the message,
 * and the exact number is one hover away in the tooltip.
 */

export interface BarDatum {
  date: string;
  value: number;
  /** Renders ghosted — the day's data is known to be incomplete. */
  partial?: boolean;
  /** Tooltip text. Native `title`, because a custom one would earn nothing here. */
  label: string;
}

export function BarChart({ data, format }: { data: BarDatum[]; format: (value: number) => string }) {
  const max = Math.max(...data.map((d) => d.value), 0);

  return (
    <div className="chartwrap">
      <div className="yaxis">
        <span>{format(max)}</span>
        <span>{format(max / 2)}</span>
        <span>0</span>
      </div>
      <div className="chart-main">
        <div className="chart">
          {/* `chart-bar`, not `bar`: styles.css already owns `.bar` for the
              part meter, and a collision there would restyle both. */}
          {data.map((d) => (
            <div className="chart-bar" key={d.date} title={d.label}>
              <div
                className={'chart-fill' + (d.partial ? ' chart-partial' : '')}
                /**
                 * Guard the divide: a series of all zeros (a quiet week, a
                 * brand-new account) would otherwise be `0/0` and every bar
                 * would render `NaN%` — which browsers drop, collapsing the
                 * chart to an empty box that looks broken rather than calm.
                 */
                style={{ height: max > 0 ? `${(d.value / max) * 100}%` : '0%' }}
              />
            </div>
          ))}
        </div>
        <div className="xaxis">
          <span>{data[0]?.date ?? '—'}</span>
          <span>{data.at(-1)?.date ?? '—'}</span>
        </div>
      </div>
    </div>
  );
}
